import { Children, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Horizontal, swipeable row of cards. Uses the browser's own scrolling with
// scroll-snap, so finger swipes in the Android WebView feel native (momentum,
// snapping to a card) without a carousel library. The arrow buttons are for
// mouse users and appear only when there is more to scroll to.
export default function DeviceCarousel({
  children,
  itemWidth = { xs: 220, sm: 240 },
  gap = 2,
  arrowLabels = { previous: "Previous", next: "Next" },
}) {
  const trackRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setCanScroll({
      left: track.scrollLeft > 4,
      right: track.scrollLeft < maxScroll - 4,
    });
  }, []);

  useEffect(() => {
    updateArrows();
    const track = trackRef.current;
    if (!track) return undefined;
    track.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      track.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, children]);

  // Move by roughly one screen of cards
  const scrollByPage = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: "smooth" });
  };

  const arrowSx = (side) => ({
    position: "absolute",
    top: "50%",
    [side]: { xs: 0, md: -8 },
    transform: "translateY(-50%)",
    zIndex: 2,
    width: 56,
    height: 56,
    bgcolor: "background.paper",
    boxShadow: 3,
    "&:hover": { bgcolor: "background.paper" },
  });

  return (
    <Box sx={{ position: "relative" }}>
      {canScroll.left && (
        <IconButton
          aria-label={arrowLabels.previous}
          onClick={() => scrollByPage(-1)}
          sx={arrowSx("left")}
        >
          <ChevronLeftIcon sx={{ fontSize: 32 }} />
        </IconButton>
      )}

      <Box
        ref={trackRef}
        sx={{
          display: "flex",
          gap,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          scrollPaddingLeft: 4,
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          // room for the card shadows; scrollbar hidden (swipe or use the arrows)
          px: 0.5,
          py: 1.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {Children.map(children, (child) =>
          child ? (
            <Box
              sx={{
                flex: "0 0 auto",
                width: itemWidth,
                scrollSnapAlign: "start",
                display: "flex",
              }}
            >
              <Box sx={{ width: "100%" }}>{child}</Box>
            </Box>
          ) : null,
        )}
      </Box>

      {canScroll.right && (
        <IconButton
          aria-label={arrowLabels.next}
          onClick={() => scrollByPage(1)}
          sx={arrowSx("right")}
        >
          <ChevronRightIcon sx={{ fontSize: 32 }} />
        </IconButton>
      )}
    </Box>
  );
}
